import { addDays, differenceInCalendarDays, endOfDay, startOfDay } from 'date-fns';

import { FilterNumericValue } from '../../../cdl/Filter/FilterNumeric/FilterNumeric';
import { NumericOperator } from '../../../cdl/Filter/FilterNumeric/numericOperator';
import { MILLISECONDS_IN_DAY, MILLISECONDS_IN_MINUTE } from '../../../common/time.constants';

interface ExpirationApiParams {
    validUntilFrom?: string;
    validUntilTo?: string;
}

const getStartOfDay = (date: Date, days = 0): Date => startOfDay(addDays(date, days));
const getEndOfDay = (date: Date, days = 0): Date => endOfDay(addDays(date, days));

const clampDaysUntil = (target: string, now: Date): number => {
    const differenceInDays = differenceInCalendarDays(startOfDay(new Date(target)), startOfDay(now));
    return Math.max(0, Math.ceil(differenceInDays));
};

export const toApiParams = (expirationFilter?: FilterNumericValue, now = new Date()): ExpirationApiParams => {
    if (!expirationFilter) {
        return { validUntilFrom: undefined, validUntilTo: undefined };
    }

    switch (expirationFilter.operator) {
        case NumericOperator.GREATER_THAN:
            return {
                validUntilFrom: getStartOfDay(now, expirationFilter.value + 1).toISOString(),
                validUntilTo: undefined,
            };
        case NumericOperator.LESS_THAN:
            return {
                validUntilFrom: now.toISOString(),
                validUntilTo: getEndOfDay(now, expirationFilter.value - 1).toISOString(),
            };
        case NumericOperator.EQUAL_TO:
            return {
                validUntilFrom: getStartOfDay(now, expirationFilter.value).toISOString(),
                validUntilTo: getEndOfDay(now, expirationFilter.value).toISOString(),
            };
    }
};

export const fromApiParams = (
    validUntilFrom?: string,
    validUntilTo?: string,
    now = new Date()
): FilterNumericValue | undefined => {
    if (!validUntilFrom && !validUntilTo) {
        return undefined;
    }

    if (!validUntilTo) {
        if (!validUntilFrom) {
            return undefined;
        }

        return {
            operator: NumericOperator.GREATER_THAN,
            value: Math.max(0, clampDaysUntil(validUntilFrom, now) - 1),
        };
    }

    const daysUntilTo = clampDaysUntil(validUntilTo, now);

    const fromDate = validUntilFrom ? startOfDay(new Date(validUntilFrom)).valueOf() : undefined;
    const toDate = endOfDay(new Date(validUntilTo)).valueOf();
    const hasOneDayRange =
        fromDate !== undefined && Math.abs(toDate - fromDate - MILLISECONDS_IN_DAY + 1) < MILLISECONDS_IN_MINUTE;

    return {
        operator: hasOneDayRange ? NumericOperator.EQUAL_TO : NumericOperator.LESS_THAN,
        value: hasOneDayRange ? daysUntilTo : daysUntilTo + 1,
    };
};
